import { useEffect, useState } from "react";
import { api } from "../api/client.js";
import { getAdminToken } from "./auth.js";

const emptyTerms = {
  title: "",
  content: "",
  version: 1,
  lastUpdated: "",
};

export default function LegalTermsManager({ onUnauthorized }) {
  const [terms, setTerms] = useState(emptyTerms);
  const [draft, setDraft] = useState(emptyTerms);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    const controller = new AbortController();

    const loadTerms = async () => {
      setLoading(true);
      setError("");
      setNotice("");

      try {
        const document = await api.admin.legal.terms.get(getAdminToken(), {
          signal: controller.signal,
        });
        setTerms(document);
        setDraft(document);
      } catch (requestError) {
        if (requestError.name === "AbortError") return;
        if (requestError.status === 401 || requestError.status === 403) {
          onUnauthorized();
          return;
        }
        setError(requestError.message || "Could not load legal terms");
      } finally {
        if (controller.signal.aborted) return;
        setLoading(false);
      }
    };

    loadTerms();

    return () => controller.abort();
  }, [onUnauthorized]);

  const handleChange = ({ target: { name, value } }) => {
    setDraft((current) => ({ ...current, [name]: value }));
    setError("");
    setNotice("");
  };

  const handleCancel = () => {
    setDraft(terms);
    setError("");
    setNotice("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setError("");
    setNotice("");

    try {
      const updated = await api.admin.legal.terms.update(
        {
          title: draft.title,
          content: draft.content,
        },
        getAdminToken(),
      );
      setTerms(updated);
      setDraft(updated);
      setNotice("Terms & Conditions saved.");
    } catch (requestError) {
      if (requestError.status === 401 || requestError.status === 403) {
        onUnauthorized();
        return;
      }
      const validationMessage = requestError.details
        ?.map((detail) => detail.message)
        .join(" ");
      setError(
        validationMessage || requestError.message || "Could not save terms",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="admin-panel admin-legal-panel">
      <div className="admin-panel-heading">
        <div>
          <p className="admin-eyebrow">Legal</p>
          <h2>Terms & Conditions</h2>
        </div>
        {terms.lastUpdated && (
          <small>
            Version {terms.version} updated{" "}
            {new Date(terms.lastUpdated).toLocaleString()}
          </small>
        )}
      </div>

      {loading ? (
        <p className="admin-empty-copy" role="status">
          Loading terms...
        </p>
      ) : (
        <form className="admin-settings-form" onSubmit={handleSubmit}>
          <label>
            <span>Title</span>
            <input
              type="text"
              name="title"
              value={draft.title}
              onChange={handleChange}
              maxLength={120}
              required
            />
          </label>

          <label>
            <span>Content</span>
            <textarea
              className="admin-legal-editor"
              name="content"
              value={draft.content}
              onChange={handleChange}
              maxLength={50000}
              required
            />
          </label>

          {error && (
            <p className="admin-form-error" role="alert">
              {error}
            </p>
          )}
          {notice && (
            <p className="admin-form-notice" role="status">
              {notice}
            </p>
          )}

          <div className="admin-form-actions">
            <button type="submit" disabled={submitting}>
              {submitting ? "Saving..." : "Save terms"}
            </button>
            <button
              type="button"
              className="admin-secondary"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
